import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchBySlug } from '../lib/cms';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { 
  Target, 
  Heart, 
  Activity, 
  Leaf, 
  Sparkles, 
  Compass, 
  Check, 
  Award, 
  Users, 
  TrendingUp, 
  Shield, 
  Smile,
  BrainCircuit,
  Eye,
  Phone,
  Mail
} from 'lucide-react';

export default function Paedagogik() {
  useDocumentMeta(
    'Pædagogik | LIVSLYST ApS',
    'Læs om vores pædagogiske målsætning, tilgange og metoder hos LIVSLYST ApS. Vi arbejder med anerkendende tilgang, Low Arousal, NADA og natur/dyr.'
  );

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchBySlug('da', 'paedagogik');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-stone-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const title = pageData?.title?.da || pageData?.title || 'Pædagogik';
  const intro = pageData?.intro?.da || pageData?.intro || 'Vi arbejder ud fra et anerkendende menneskesyn, hvor den enkelte mødes med respekt, ligeværdighed og forståelse.';

  // 1. Vores pædagogiske målsætning
  const goalsList = [
    'Den enkelte oplever tryghed, anerkendelse og struktur',
    'Fællesskab og sociale relationer prioriteres højt',
    'Der er plads til forskellighed og individuelle behov',
    'Borgeren oplever medbestemmelse og indflydelse på eget liv',
    'Udvikling sker i et tempo, der matcher den enkeltes forudsætninger'
  ];

  const evaluationPoints = [
    'Individuelle og realistiske mål',
    'Fokus på ressourcer frem for begrænsninger',
    'Løbende støtte og opfølgning',
    'Oplevelse af udvikling og progression',
    'Sammenhæng mellem mål, indsats og trivsel'
  ];

  // 2. Pædagogiske tilgange og metoder (Intro points)
  const introMethodsPoints = [
    'Alle borgere mødes individuelt',
    'Støtten tilpasses den enkeltes behov',
    'Relationer prioriteres højt i det pædagogiske arbejde',
    'Der arbejdes med struktur, tydelighed og tryghed',
    'Udvikling sker med udgangspunkt i den enkeltes tempo'
  ];

  // The 5 core approaches
  const approaches = [
    {
      id: 'anerkendende',
      title: 'Anerkendende og jeg-støttende tilgang',
      icon: Heart,
      color: 'emerald',
      bg: 'bg-emerald-50/60',
      border: 'border-emerald-100',
      iconBg: 'bg-emerald-100 text-emerald-800',
      description: 'Vi arbejder med jeg-støttende samtaler, hvor fokus er at understøtte borgerens identitet, selvforståelse og evne til at mestre eget liv. Gennem nærværende dialog og guidning hjælper vi borgeren med at skabe struktur, overskuelighed og tro på egne ressourcer. Vi møder borgeren med respekt, tydelighed og anerkendelse, og vi arbejder aktivt for at skabe trygge relationer, som danner grundlag for udvikling og trivsel.',
      bullets: [
        'At blive mødt med anerkendelse frem for fordømmelse',
        'At opleve trygge og stabile relationer',
        'At få støtte til at skabe struktur i hverdagen',
        'At styrke identitet og selvforståelse',
        'At opleve øget mestring af eget liv'
      ]
    },
    {
      id: 'low-arousal',
      title: 'Low Arousal',
      icon: Activity,
      color: 'amber',
      bg: 'bg-amber-50/40',
      border: 'border-amber-100',
      iconBg: 'bg-amber-100 text-amber-800',
      description: 'Vi anvender Low Arousal som pædagogisk tilgang med fokus på at forebygge konflikter, mindske stress og skabe ro og tryghed omkring borgeren. Vi har opmærksomhed på at tilpasse krav, kommunikation og omgivelser, så den enkelte ikke overbelastes. Gennem een rolig, afstemt og konfliktnedtrappende tilgang arbejder vi for at skabe et miljø, hvor borgeren oplever sig mødt, forstået og støttet fremfor korrigeret.',
      bullets: [
        'Mindre stress og overstimulering',
        'Øget følelsesmæssig regulering',
        'Oplevelse af tryghed og stabilitet',
        'Forebyggelse af konflikter',
        'Bedre mulighed for deltagelse i fællesskabet'
      ]
    },
    {
      id: 'natur-dyr',
      title: 'Natur, dyr og sansemotoriske oplevelser',
      icon: Leaf,
      color: 'teal',
      bg: 'bg-teal-50/50',
      border: 'border-teal-100',
      iconBg: 'bg-teal-100 text-teal-800',
      description: 'Naturen og arbejdet med dyr er en central del af vores lærings- og udviklingsmiljø. Vi oplever, at naturen skaber ro, nærvær og mulighed for sanselige oplevelser, som kan understøtte både mental trivsel og personlig udvikling. Vi arbejder med sansemotorisk stimuli gennem aktiviteter, der styrker kropsbevidsthed, regulering, koncentration og velvære. Det kan blandt andet ske gennem fysisk aktivitet, kontakt med dyr, kreative opgaver og ophold i naturen. Vi tror på værdien af “højt til loftet” — både fysisk og mentalt. Naturen giver mulighed for pauser fra krav og overstimulering og kan skabe rum for refleksion, deltagelse og positive fællesskaber.',
      bullets: [
        'Øget ro og trivsel',
        'Bedre regulering af sanseindtryk',
        'Øget kropsbevidsthed',
        'Flere positive fællesskaber',
        'Mulighed for succesoplevelser gennem praktiske aktiviteter'
      ]
    },
    {
      id: 'nada',
      title: 'NADA og regulerende værktøjer',
      icon: Sparkles,
      color: 'sky',
      bg: 'bg-sky-50/40',
      border: 'border-sky-100',
      iconBg: 'bg-sky-100 text-sky-800',
      description: 'Som en del af vores holistiske indsats anvender vi NADA som et understøttende redskab til ro, regulering og trivsel. NADA kan bidrage til øget kropslig og mental balance samt støtte borgeren i at finde ro og stabilitet i hverdagen. Vi arbejder generelt med regulerende og støttende redskaber, der kan hjælpe den enkelte til større selvforståelse, trivsel og deltagelse i fællesskabet.',
      bullets: [
        'Mental og kropslig balance',
        'Øget ro og stabilitet',
        'Bedre trivsel i hverdagen',
        'Øget selvforståelse',
        'Mulighed for bedre deltagelse i fællesskabet'
      ]
    },
    {
      id: 'individuel-udvikling',
      title: 'Individuel udvikling og medbestemmelse',
      icon: Compass,
      color: 'emerald',
      bg: 'bg-emerald-50/40',
      border: 'border-emerald-100',
      iconBg: 'bg-emerald-100 text-emerald-800',
      description: 'Vi tager udgangspunkt i den enkeltes ressourcer og arbejder med realistiske og meningsfulde mål, hvor succesoplevelser og progression er centrale elementer. Borgeren inddrages aktivt i egen udvikling gennem medbestemmelse og samarbejde omkring mål og indsatser. Vi tror på, at udvikling bedst skabes i et trygt miljø, hvor den enkelte oplever sig set, hørt og forstået.',
      bullets: [
        'Har indflydelse på egen hverdag',
        'Deltager aktivt i mål og udviklingsprocesser',
        'Oplever ejerskab over egen udvikling',
        'Mødes med respekt for individuelle behov',
        'Udvikler selvstændighed og ansvarsfølelse'
      ]
    }
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-stone-100 via-stone-50 to-white py-20 md:py-28 border-b border-stone-200 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 relative z-10"
        >
          <span className="inline-flex items-center gap-1.5 text-emerald-800 text-xs font-semibold tracking-wider uppercase">
            <Award className="w-3.5 h-3.5" /> Vores pædagogiske fundament
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-stone-900 tracking-tight leading-none">
            {title}
          </h1>
          <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed font-medium">
            {intro}
          </p>
        </motion.div>
      </section>

      {/* 1. Vores pædagogiske målsætning */}
      <section className="py-20 md:py-28 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Målsætning & Evaluering
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Vores pædagogiske målsætning
            </h2>
            <div className="h-1 w-20 bg-emerald-700 mx-auto rounded-full mt-2" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Core Workplace Goals */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 space-y-6"
            >
              <div className="bg-stone-50 p-8 rounded-2xl border border-stone-200 shadow-sm space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-100 text-emerald-800 rounded-xl">
                    <Target className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900">
                    Vores mål er at skabe en arbejdsplads, hvor:
                  </h3>
                </div>

                <div className="space-y-4">
                  {goalsList.map((goal, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-stone-100 shadow-xs">
                      <div className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                      <p className="text-stone-700 text-sm md:text-base font-medium leading-relaxed">
                        {goal}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right Column: Development, SMART-goals & nearest zone */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 space-y-6"
            >
              <div className="space-y-6 text-stone-600 leading-relaxed">
                <p className="text-base md:text-lg font-medium text-stone-800">
                  Vi arbejder målrettet med borgernes udvikling gennem individuelle mål, handleplaner og løbende opfølgning og evaluering.
                </p>
                <p className="text-sm md:text-base">
                  Den pædagogiske indsats tager blandt andet udgangspunkt i <strong>zonen for nærmeste udvikling</strong>, hvor fokus er på realistiske mål og positive erfaringer, der styrker troen på egne evner.
                </p>
                <p className="text-sm md:text-base">
                  Gennem arbejdet med <strong>SMART-mål</strong>, trivselssamtaler og faglig refleksion sikrer vi en kontinuerlig, realistisk og målbar evaluering af indsatsen, så den enkelte borger oplever progression, motivation og sammenhæng i sin udvikling.
                </p>

                <div className="pt-4 border-t border-stone-100 space-y-3">
                  <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider">
                    Fokusområder i evalueringen:
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {evaluationPoints.map((point, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-stone-700 text-sm font-medium">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Highlighted Banner */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-16 bg-gradient-to-r from-emerald-800 to-emerald-900 text-white p-8 md:p-10 rounded-2xl shadow-md text-center space-y-4 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-700/20 rounded-full blur-3xl -ml-20 -mb-20 pointer-events-none" />
            
            <p className="text-lg md:text-xl font-semibold max-w-4xl mx-auto leading-relaxed relative z-10">
              ”Hos Livslyst er vores overordnede målsætning at understøtte livskvalitet, selvstændighed og deltagelse i fællesskabet gennem en meningsfuld og udviklende hverdag.”
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. Pædagogiske tilgange og metoder (Intro & Core points) */}
      <section className="py-20 md:py-28 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:items-stretch items-center">
            {/* Text Column */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 space-y-6"
            >
              <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
                Vores Tilgang
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Pædagogiske tilgange og metoder
              </h2>
              <div className="h-1 w-20 bg-emerald-700 rounded-full mt-2" />
              
              <p className="text-base md:text-lg text-stone-700 leading-relaxed font-medium">
                Hos Livslyst ApS arbejder vi ud fra et holistisk menneskesyn og en anerkendende pædagogisk tilgang, hvor relationen, trygheden og den enkeltes ressourcer er i centrum.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Vi tilpasser vores støtte og indsats til den enkelte borgers behov, forudsætninger og udviklingspotentiale. Dette sikrer, at alle mødes med værdighed og får de bedste rammer for trivsel.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {introMethodsPoints.map((point, idx) => (
                  <div key={idx} className="flex items-start gap-2.5 bg-white p-3.5 rounded-xl border border-stone-200 shadow-2xs">
                    <Check className="w-4.5 h-4.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-stone-700 text-sm font-semibold">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Image Column */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5 lg:h-full"
            >
              <div className="relative h-full min-h-[350px] lg:min-h-0 flex">
                <div className="absolute inset-0 bg-emerald-800 rounded-2xl rotate-2 scale-95 opacity-5" />
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/img-7376_1.jpeg"
                  alt="Fællesskab og trivsel hos Livslyst"
                  className="relative z-10 w-full h-full min-h-[350px] lg:min-h-0 object-cover rounded-2xl shadow-md border border-stone-200"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The 5 Core Approaches (Detailed Sections) */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Faglige Metoder
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Dybdegående indsigt i vores metoder
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Vi anvender specialiserede og anerkendte pædagogiske redskaber, der understøtter den enkelte borgers personlige rejse.
            </p>
            <div className="h-1 w-20 bg-emerald-700 mx-auto rounded-full mt-2" />
          </div>

          <div className="space-y-16 md:space-y-24">
            {approaches.map((approach, idx) => {
              const IconComponent = approach.icon;
              const isEven = idx % 2 === 0;

              return (
                <motion.div 
                  key={approach.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${
                    isEven ? '' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Text & Bullets Column */}
                  <div className={`lg:col-span-7 space-y-6 ${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl ${approach.iconBg}`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <h3 className="text-2xl font-bold text-stone-900 tracking-tight">
                        {approach.title}
                      </h3>
                    </div>

                    <p className="text-stone-600 text-base leading-relaxed">
                      {approach.description}
                    </p>

                    <div className="bg-stone-50 p-6 rounded-xl border border-stone-200 space-y-3">
                      <h4 className="font-bold text-stone-900 text-xs uppercase tracking-wider">
                        Nøgleeffekter og fokus:
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {approach.bullets.map((bullet, bIdx) => (
                          <div key={bIdx} className="flex items-start gap-2 text-stone-700 text-sm font-medium">
                            <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* No inline buttons here anymore, moved to a dedicated CTA section below */}
                  </div>

                  {/* Image Column */}
                  <div className={`lg:col-span-5 ${!isEven ? 'lg:order-1' : ''}`}>
                    <div className="relative">
                      <div className="absolute inset-0 bg-emerald-800 rounded-2xl rotate-1 scale-95 opacity-5" />
                      <img
                        src={
                          approach.id === 'anerkendende' 
                            ? 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/img-7064.jpeg'
                            : approach.id === 'low-arousal'
                            ? 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/att-xwikymmkma4s0e4van71nw0ooruupomtqe9ii8ymbxg.jpeg'
                            : approach.id === 'natur-dyr'
                            ? 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/img-7407.jpeg'
                            : approach.id === 'nada'
                            ? 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/fullsizerender.jpeg'
                            : 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/fullsizerender2.jpeg'
                        }
                        alt={approach.title}
                        className="relative z-10 w-full h-[320px] object-cover rounded-2xl shadow-md border border-stone-200"
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Centered CTA Section */}
      <section className="py-16 md:py-24 bg-stone-50 border-t border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="bg-stone-900 text-white rounded-2xl p-8 md:p-12 shadow-xl relative overflow-hidden"
          >
            {/* Decorative background blur */}
            <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-emerald-800/30 blur-3xl" />
            
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-4 text-center lg:text-left">
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                  Vil du høre mere om vores pædagogiske arbejde?
                </h3>
                <p className="text-stone-300 text-sm md:text-base leading-relaxed">
                  Vi står altid klar til en uforpligtende snak om vores metoder, værdigrundlag eller en rundvisning i vores trygge rammer i Brørup. Kontakt os i dag, og lad os tage en snak om mulighederne.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col justify-center items-center lg:items-end gap-4 w-full">
                <div className="w-full flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/vaerdigrundlag"
                    className="flex-1 text-center px-5 py-3 bg-stone-800 hover:bg-stone-700 text-white border border-stone-700 rounded-lg font-semibold shadow-md transition-all text-sm whitespace-nowrap"
                  >
                    Vores kerneværdier
                  </Link>
                  <Link
                    to="/kontakt"
                    className="flex-1 text-center px-5 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-md transition-all text-sm whitespace-nowrap"
                  >
                    Kontakt os nu
                  </Link>
                </div>
                <div className="flex flex-row flex-wrap justify-center lg:justify-end gap-x-4 gap-y-1 text-xs text-stone-400 w-full">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <Phone className="w-3.5 h-3.5 text-emerald-500" />
                    Tlf: <a href="tel:51504654" className="hover:text-white transition-colors">51 50 46 54</a>
                  </span>
                  <span className="hidden sm:inline text-stone-600">|</span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <Mail className="w-3.5 h-3.5 text-emerald-500" />
                    Email: <a href="mailto:Kontakt@livslystaps.dk" className="hover:text-white transition-colors">Kontakt@livslystaps.dk</a>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
